import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { templateService } from '../../services/api';
import { Template, CreateTemplateData } from '../../types';

interface CreateTemplateModalProps {
  onClose: () => void;
  onTemplateCreated: (template: Template) => void;
}

interface FormData {
  name: string;
  width: number;
  height: number;
  backgroundColor: string;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  onClose,
  onTemplateCreated,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      width: 800,
      height: 600,
      backgroundColor: '#ffffff',
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError('');

    try {
      const templateData: CreateTemplateData = {
        name: data.name,
        width: data.width,
        height: data.height,
        backgroundColor: data.backgroundColor,
        elements: [],
      };

      const response = await templateService.createTemplate(templateData);
      onTemplateCreated(response.template);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Template oluşturulurken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Yeni Template Oluştur
          </h3>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Template Adı
              </label>
              <input
                {...register('name', { required: 'Template adı gerekli' })}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Örn: Instagram Post"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Genişlik (px)
                </label>
                <input
                  {...register('width', {
                    required: 'Genişlik gerekli',
                    min: { value: 100, message: 'Minimum 100px' },
                    max: { value: 3000, message: 'Maximum 3000px' },
                  })}
                  type="number"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                {errors.width && (
                  <p className="mt-1 text-sm text-red-600">{errors.width.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Yükseklik (px)
                </label>
                <input
                  {...register('height', {
                    required: 'Yükseklik gerekli',
                    min: { value: 100, message: 'Minimum 100px' },
                    max: { value: 3000, message: 'Maximum 3000px' },
                  })}
                  type="number"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                {errors.height && (
                  <p className="mt-1 text-sm text-red-600">{errors.height.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Arka Plan Rengi
              </label>
              <input
                {...register('backgroundColor')}
                type="color"
                className="mt-1 block w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
              >
                {isLoading ? 'Oluşturuluyor...' : 'Oluştur'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateModal;
